import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';

interface WaitlistEntry {
  id: string;
  name: string | null;
  email: string;
  company: string | null;
  phone: string | null;
  message: string | null;
  created_at: string | null;
}

export default function AdminDashboard() {
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, isAdmin, signOut, loading: authLoading } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    document.head.appendChild(metaRobots);
    document.title = 'Admin Dashboard | NoQ5.ai';
    return () => {
      document.head.removeChild(metaRobots);
    };
  }, []);

  useEffect(() => {
    if (user && isAdmin) {
      fetchWaitlistEntries();
    }
  }, [user, isAdmin]);

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card>
          <CardContent className="pt-6">
            <p className="text-destructive">Access denied. Admin privileges required.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const fetchWaitlistEntries = async () => {
    try {
      const { data, error } = await supabase
        .from('waitlist')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        // Don't expose internal database error details
        console.error('Database error fetching waitlist:', error);
        throw new Error('Unable to load waitlist');
      }
      
      setWaitlistEntries(data || []);
    } catch (error) {
      // Generic user-facing error message
      toast({
        title: "Unable to load data",
        description: "Please refresh the page or contact support if the problem persists.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    toast({
      title: "Signed out",
      description: "You have been signed out successfully.",
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading waitlist entries...</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-muted-foreground">Manage waitlist submissions</p>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline">
              <a href="/admin/visitors">Visitors →</a>
            </Button>
            <Button onClick={handleSignOut} variant="outline">Sign Out</Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Waitlist Submissions
              <Badge variant="secondary">{waitlistEntries.length} total</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {waitlistEntries.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No waitlist submissions yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Submitted</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {waitlistEntries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="font-medium">
                          {entry.name || 'N/A'}
                        </TableCell>
                        <TableCell>{entry.email}</TableCell>
                        <TableCell>{entry.company || 'N/A'}</TableCell>
                        <TableCell>{entry.phone || 'N/A'}</TableCell>
                        <TableCell className="max-w-xs truncate">
                          {entry.message || 'N/A'}
                        </TableCell>
                        <TableCell>{formatDate(entry.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}